import React, { useState } from 'react';
import ConfirmDialog from '../ConfirmDialog';
import { Menu, MenuItem } from '../Menu';
import { useMoneyInParenthesesFormat, useDateFormat } from '../../context/LocaleContext';
import { familyDisplayName } from '../../domain/people';
import { canVoidEntry, ledgerPillMinor, ledgerRowView, voidConfirmMessage } from '../../domain/familyAccount';
import s from './families.module.css';
import t from '../SimpleTable.module.css';
import { DateInput } from '../RegionInputs';
import Unread from '../Unread';
import { READING_WORDING as RW } from '../../domain/reading';

// Every family's ledger in one date range — Families & Invoices → Transactions.
//
// ── The row menu (#191) ────────────────────────────────────────────────────
//
// MMS's per-row `⋮` on this tab is source-dependent:
//
//   | row                                   | `⋮` contents                        |
//   |---------------------------------------|-------------------------------------|
//   | badge `Lesson`                        | Edit Attendance, Edit Lesson Details |
//   | badge `Package`, or hand-entered      | Edit, Delete Transaction             |
//
// So MMS refuses to delete a calendar-generated charge from this list and sends
// you to the lesson instead, as `canVoidEntry` does. This menu has Delete
// Transaction only: there is no Edit Transaction here, and the package half is
// refused (`canVoidEntry` in src/domain/familyAccount.js). MMS's edit item reads
// Edit here and Edit Transaction on the family ledger; Delete Transaction on
// both.
//
// `onVoidEntry` is optional and the menu is absent without it; FamiliesPage
// passes it (#309).
//
// ── The Transaction pill (#635) ────────────────────────────────────────────
//
// MMS writes the kind as a word, capitalised, in a coloured chip: `Charge
// $30.00`. The word and the colour are the core's `ledgerRowView`, the family
// ledger's own pill, and the colour says which way the entry moves the balance:
// red for one that raises it, green for one that lowers it. MMS's charge chip
// is red; the colour of its payment chip is unrecorded, so the green is ours.
//
// The figure is the family ledger's (#649): a discount and a refund in
// parentheses, `Discount ($10.00)`, `Refund ($5.00)`, as MMS's Transactions tab
// writes them, through `ledgerPillMinor`.
const PILL = { owe: s.owe, credit: s.credit };

function Pill({ entry, money }) {
  const pill = ledgerRowView(entry);
  return <span className={PILL[pill.tone] || s.owe}>{pill.label} {money}</span>;
}

// `read`: the ledger has answered (#629). Until it has, an empty range says
// nothing rather than "No transactions in this range."
export default function TransactionList({ rows, read = true, failed = false, families, range, onRangeChange, onVoidEntry }) {
  const formatPill = useMoneyInParenthesesFormat();
  const shortDate = useDateFormat();
  const familyName = (id) => {
    const f = families.find((x) => x.id === id);
    return f ? familyDisplayName(f) : '–';
  };

  // Two pieces of state rather than one, the same shape FamilyDetail.jsx uses:
  // the menu closes the instant an item is clicked, but the dialog it opened
  // has to survive that close.
  const [rowMenuId, setRowMenuId] = useState(null);
  const [confirmEntry, setConfirmEntry] = useState(null);

  const menus = Boolean(onVoidEntry);
  const columns = menus ? 5 : 4;
  const closeMenu = () => setRowMenuId(null);
  const confirmVoid = async () => {
    const entry = confirmEntry;
    setConfirmEntry(null);
    if (entry) await onVoidEntry?.(entry.id);
  };

  return (
    <>
      <div className={s.bar}>
        <span className={s.sub}>Showing transactions between</span>
        <DateInput
          className={s.dateInput}
          value={range.start}
          onChange={(e) => onRangeChange({ ...range, start: e.target.value })}
        />
        <span className={s.sub}>and</span>
        <DateInput
          className={s.dateInput}
          value={range.end}
          onChange={(e) => onRangeChange({ ...range, end: e.target.value })}
        />
      </div>

      <div className={t.wrap}>
        <table className={t.table}>
          <thead>
            <tr>
              <th>Date</th><th>Family</th><th>Transaction</th><th>Description</th>
              {menus ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id}>
                {/* Substituted into, not parsed: see formatDateOnly. */}
                <td>{shortDate(e.occurredOn) || e.occurredOn}</td>
                <td>{familyName(e.familyId)}</td>
                <td><Pill entry={e} money={formatPill(ledgerPillMinor(e))} /></td>
                <td>{e.description || '–'}</td>
                {menus ? (
                  <td className={s.rowMenuCell}>
                    {canVoidEntry(e) ? (
                      <Menu
                        open={rowMenuId === e.id}
                        onClose={closeMenu}
                        trigger={(
                          <button
                            className={s.kebab}
                            aria-label="Row actions"
                            onClick={() => setRowMenuId((cur) => (cur === e.id ? null : e.id))}
                          >
                            ⋮
                          </button>
                        )}
                      >
                        <MenuItem
                          icon="trash"
                          onClick={() => { closeMenu(); setConfirmEntry(e); }}
                        >
                          Delete Transaction
                        </MenuItem>
                      </Menu>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr><td colSpan={columns} className={t.muted}>
                {read ? RW['transactions.emptyRange'] : <Unread as="span" failed={failed} />}
              </td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* The confirm names the family as well as the amount, kind and date —
          two families' rows sit next to each other here. See voidConfirmMessage. */}
      <ConfirmDialog
        open={Boolean(confirmEntry)}
        title="Delete Transaction"
        message={voidConfirmMessage(confirmEntry, {
          familyName: confirmEntry ? familyName(confirmEntry.familyId) : '',
          amountText: confirmEntry ? formatPill(ledgerPillMinor(confirmEntry)) : '',
          dateText: confirmEntry ? shortDate(confirmEntry.occurredOn) : '',
        })}
        confirmLabel="Delete"
        discardLabel="Cancel"
        cancelLabel=""
        onDiscard={() => setConfirmEntry(null)}
        onCancel={() => setConfirmEntry(null)}
        onConfirm={confirmVoid}
      />
    </>
  );
}
